
import Vue from 'vue'
import InitLoader from '@/components/UI/Loader/InitLoader.vue'
import { EPartnerStoresStructureType } from '@/enums/EPartnerStoresStructureType';
import PartnerSettings from '@/models/PartnerSettings';
import GoogleMap from "@/components/Map/GoogleMap.vue";

import { mapGetters, mapState } from 'vuex';
import User from '@/models/User';
import Store from '@/models/Store';
import StoreService from '@/services/storeService';
import routerManager from '@/routerManagerNew/routerManager';
import { ETypeOfBranchServices } from '@/models/ETypeOfBranchServices';
import EmptyView from "@/components/Cart/EmptyCart.vue";
import LocationMapCard from "@/components/Map/LocationCard.vue";
import StoreCard from '@/components/Store/StoreCard.vue'
import _requestHelper from '@/apiManager/_requestHelper';
import { Apis } from '@/apiManager/Apis';
import CloseIcon from '@/components/UI/Icons/CloseIcon.vue';
import {
    buildCanonicalUrl,
    buildCollectionPageJsonLd,
    buildSeoMeta,
    buildSeoTitle,
    getRuntimeSeoConfig
} from '@/helpers/seoMetaHelper';

export default Vue.extend({
    components: {
        "init-loader": InitLoader,
        "google-map": GoogleMap,
        "store-card": StoreCard,
        "empty-view": EmptyView,
        "location-map-card": LocationMapCard,
        CloseIcon
    },

    props: {
        openedFromDialog: {
            default: false,
            type: Boolean,
            required: false
        },

        isDiaologOpened: {
            default: false,
            type: Boolean,
            required: false
        }                
    },

    data: () => ({
        loading: false as boolean,
        isFromLandingPage: false as boolean,
        isPageIsReloaded: false as boolean,
        isFromShopPages: false as boolean,
        hoveredStoreId: 0 as number,
        addressDialogState: false as boolean,
        listOfEligibleStores: [] as Store[]
    }),

    metaInfo: function() {
        const partnerSettings = (this as any).partnerSettings || {};
        const vueAppConfig = getRuntimeSeoConfig();
        const partnerName = partnerSettings.partnerName || vueAppConfig.VUE_APP_Title || "Online Store";
        const title = buildSeoTitle({
            primary: "Stores",
            siteName: partnerName,
            descriptor: "Choose Store"
        });
        const description = `Browse available ${partnerName} stores and choose where to shop online.`;
        const image =
            partnerSettings.seoImageUrl ||
            partnerSettings.appBarLogoUrl ||
            partnerSettings.logoUrl ||
            "";
        const canonicalUrl = buildCanonicalUrl(this);

        return buildSeoMeta({
            title,
            description,
            keywords: partnerSettings.seoKeywords || vueAppConfig.VUE_APP_Meta_Keywords || "",
            image,
            canonicalUrl,
            structuredData: buildCollectionPageJsonLd({
                name: title,
                description,
                url: canonicalUrl,
                image,
                siteName: partnerName
            })
        });
    },

    computed: {
        ...mapState("ui", ["isMobileViewDisplayed"]),
        ...mapState("partner", ["partnerSettings", "appDataContent"]),
        ...mapState("auth", ["preferredOrderType"]),
        ...mapState("store", ["listOfStores", "listOfDeliveryStores", "selectedStoreDetails"]),
        
        ...mapGetters("auth", ["currentSelectedUser"]),

        /** Define user object */
        user(): User {
            return (this as any).currentSelectedUser as User;
        },
        
        /** Check if the partner structure type is agnostic */
        isPartnerStoreStructureTypeAgnostic(): boolean {
            return ((this as any).partnerSettings as PartnerSettings).partnerStoresStructureType == EPartnerStoresStructureType.StoreAgnosticModel; 
        },

        /** Check if the partner structure type is single store */
        isPartnerStoreStructureTypeSingleStore(): boolean {
            return ((this as any).partnerSettings as PartnerSettings).partnerStoresStructureType == EPartnerStoresStructureType.SingleStoreModel; 
        },
        

        /** Check if the initial loader page is displayed */
        isInitialLoaderPageDisplayed(): boolean {
            return (this.isPartnerStoreStructureTypeAgnostic == true || this.isPartnerStoreStructureTypeSingleStore == true) && this.loading == true
        },


        /** Retrieve the markers to be displayed on map of each available branch */
        getStoresPositionsAndMarkersUrl(): any {           
            return ((this as any).listOfEligibleStores as Store[]).map(store => {                
                return {
                id: store.id,
                lat: store.address.position.latitude,
                lng: store.address.position.longitude,
                marker: store.mapMarkerImageUrl
                }
            })                   
        },

        routeReloadQuery(): string {
            return this.$route.query["_r"] as string; 
        }

    },

    methods: {

        /** Set a store selected by the user */
        setStore(storeId: number) {
            StoreService.getStoreDetails(storeId, this.isPartnerStoreStructureTypeAgnostic).then(async () => {
                // go to the store home page, or straight to the item for single-service stores
                await StoreService.navigateToStoreOrServiceItem((this as any).selectedStoreDetails);
                this.$emit('closeDialog');
            })
            .catch(() => {
                this.$emit('closeDialog');
            })
        },

        /** get list of elligible branches */
        async getListBranches() {

            // routerManager routes through this exact page as a transient stop when navigating to a
            // store home page or product details page "directly from the stores list" (see the
            // isTransientSettleHop comment in routerManagerNew/routerManager.ts) purely to let Vue
            // Router settle before firing the real navigation — it's never meant to actually run this
            // page's own fetch/auto-navigate logic. Doing so anyway raced with the caller's own pending
            // navigation and looped indefinitely (this is what used to break pickup + store selection
            // for agnostic partners).
            if((this.$route.params as any).isTransientSettleHop === true) return;

            // re-entrancy guard: the auto-navigate branches below trigger an intermediate
            // replace to this same "storesPage" route (to settle the router), which stamps a
            // fresh "_r" query value and re-fires the routeReloadQuery watcher below while this
            // call is still in flight — without this guard that recurses into an infinite loop
            if(this.loading == true) return;

            // update loading state            
            this.loading = true;

            //#region update the type of branches that needs to be fetched            
            var typeOfBranchServices = ETypeOfBranchServices.All;

            // if the partner store structure type is agnostic
            // check the previous route:
            //      if the route was the landing page, get all type of branch services
            //      else if the route is from the store home page, get only the pickup stores
            if(this.isPartnerStoreStructureTypeAgnostic == true) {

                if(this.openedFromDialog == true) {
                    typeOfBranchServices = ETypeOfBranchServices.PickupOnly;
                }        
            }

            if(typeOfBranchServices == ETypeOfBranchServices.All) {
                await StoreService.getElligibleStores();
                this.listOfEligibleStores = (this as any).listOfStores;
            }

            else {
                // get list of stores           
                var storeObject = new Store();
                await _requestHelper({
                    method: "post",
                    url: Apis.listOfDiffrentStoresURL,
                    data: {
                        shopperCoordinates: {
                            latitude: this.user.usedAddress.position.latitude,
                            longitude: this.user.usedAddress.position.longitude,
                        },
                        typeOfBranchServices: typeOfBranchServices
                    },
                    loaderKey: "",
                    successCallback: async (stores: any) => {
                        this.listOfEligibleStores = stores.map((store:any) => storeObject.fetchStoreObjectFromJson(store));
                    }
                })
            }

            if(this.listOfEligibleStores.length > 0) {
                if(typeOfBranchServices == ETypeOfBranchServices.All) {

                    // if the partner store structure type is agnostic
                    // get the first delivery branch, get it's details
                    if(this.isPartnerStoreStructureTypeAgnostic == true ) {  
                        var listOfDeliveryStores = this.listOfEligibleStores.filter(store => StoreService.checkIfStoreIsEligibileForDelivery(store));

                        if(listOfDeliveryStores != null && listOfDeliveryStores.length > 0) {
                            await StoreService.getStoreDetails(listOfDeliveryStores[0].id);      

                            // go to store home page, or straight to the item for single-service stores
                            await StoreService.navigateToStoreOrServiceItem((this as any).selectedStoreDetails);
                                                          
                        }
                    }

                    // if the partner store structure is single store
                    // go to the first available store
                    else {
                        if(this.isPartnerStoreStructureTypeSingleStore == true) {
                            await StoreService.getStoreDetails(this.listOfEligibleStores[0].id);
                            await StoreService.navigateToStoreOrServiceItem((this as any).selectedStoreDetails);
                        }
                    }
                }
            }
            
            // update loading state
            this.addressDialogState = false;        
            this.loading = false; 
        },

        closeStoresDialog() {
            this.$emit("closeDialog")
        }

    },

    async mounted() {
        await this.getListBranches();
    },

    watch: {
        async isDiaologOpened(val: boolean) {
            if(val == true) {
                await this.getListBranches();
            }
        },

        async routeReloadQuery() { 
            await this.getListBranches();
        }
    }
})
